import { Tabs } from '@any-ui-react/core';
import { useModals } from '@any-ui-react/modals';
import type { PropsWithChildren } from 'react';
import { useEffect, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';
import type { HistoryListHandle } from '@/common/components/shared/export-import/components/HistoryList';
import { ImportHistoryList } from '@/common/components/shared/export-import/import-history/ImportHistoryList';

import { ImportModalForm } from './ImportModalForm';

type ImportModalTab = 'import' | 'history';

/**
 * Trigger + modal for bulk import. Wraps its `children` (a link/button) so a click opens a modal
 * with two tabs — "Import" (the existing form, unchanged) and "History" (live import-progress list).
 * Mirrors `EmployeeListExportModal`'s controlled-effect pattern exactly: `openModal(...)` is called
 * from a `useEffect` keyed on `[opened, activeTab, ...]` rather than once from the click handler, so
 * every tab switch (manual or the auto-switch-to-History on import success) re-passes fresh `children`
 * to the same `modalId` instead of leaving the Tabs frozen on a stale snapshot. The History tab
 * renders one unified list for both import kinds (Increment 2) — it no longer needs to know which
 * kind is currently selected in the Import tab's form.
 */
export const ImportModal = ({ children }: PropsWithChildren) => {
  const modalId = useRef(uuid()).current;
  const { openModal, closeModal } = useModals();
  const [opened, setOpened] = useState(false);
  const [activeTab, setActiveTab] = useState<ImportModalTab>('import');
  const wasOpenedRef = useRef(false);
  const historyListRef = useRef<HistoryListHandle>(null);

  // Refetch the history list whenever the History tab becomes active, so the latest state shows
  // without requiring a close/reopen (mirrors `EmployeeListExportModal`'s tab-switch refetch).
  useEffect(() => {
    if (activeTab === 'history') {
      historyListRef.current?.refetch();
    }
  }, [activeTab]);

  useEffect(() => {
    if (!opened) {
      wasOpenedRef.current = false;
      closeModal(modalId);
      return;
    }

    const tab = wasOpenedRef.current ? activeTab : 'import';
    wasOpenedRef.current = true;
    if (tab !== activeTab) {
      setActiveTab(tab);
    }

    openModal({
      modalId,
      closeOnClickOutside: false,
      closeOnEscape: true,
      withinPortal: false,
      centered: false,
      title: 'Import',
      size: '1024px',
      onClose: () => setOpened(false),
      children: (
        <Tabs
          className="-mx-6 -mt-4"
          keepMounted={false}
          value={tab}
          onChange={value => setActiveTab(value === 'history' ? 'history' : 'import')}
        >
          <Tabs.List className="px-6">
            <Tabs.Tab value="import">Import</Tabs.Tab>
            <Tabs.Tab value="history">History</Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel className="px-6 pt-4" value="import">
            <ImportModalForm onClose={() => setOpened(false)} onImported={() => setActiveTab('history')} />
          </Tabs.Panel>
          <Tabs.Panel value="history">
            <ImportHistoryList ref={historyListRef} modalId={modalId} />
          </Tabs.Panel>
        </Tabs>
      ),
    });
  }, [opened, openModal, closeModal, activeTab, modalId]);

  const onClick = () => setOpened(true);

  return <span onClick={onClick}>{children}</span>;
};
